import { useNavigate } from "react-router-dom";
import { TriangleAlert } from "lucide-react";

import { Button } from "components/ui";
import { pathGenerators } from "utils/routes";

import s from "./LabOrderNotices.module.css";

// Icon, then the message, then the action on the right — the notice reads left to
// right and its action never buries itself under the text.
const Notice = ({ tone = "blocking", testId, action, children }) => (
  <div className={`${s.notice} ${s[tone]}`} role="alert" data-testid={testId}>
    <TriangleAlert size={20} className={s.icon} />
    <div className={s.body}>{children}</div>
    {!!action && <div className={s.action}>{action}</div>}
  </div>
);

// Blocking: the patient's recorded details break a rule Healthlink enforces, so no
// order can be built for them at all.
const PatientDetailsNotice = ({ patientId, violations }) => {
  const navigate = useNavigate();

  return (
    <Notice
      tone="blocking"
      testId="lab-orders-patient-notice"
      action={
        <Button
          variant="outline"
          size="small"
          onClick={() => navigate(pathGenerators.patient.details(patientId))}
        >
          Edit patient details
        </Button>
      }
    >
      <p className={s.text}>
        This patient&apos;s details can&apos;t be sent to a laboratory, so a lab
        order cannot be placed until they are corrected.
      </p>
      <ul className={s.list}>
        {violations.map((violation) => (
          <li key={violation.field}>{violation.message}</li>
        ))}
      </ul>
    </Notice>
  );
};

// Blocking: laboratory ordering is switched off for this practice. A different thing
// from "Healthlink has no laboratory for us", and the backend answers both with an
// empty facility list — so they must not share one message, or a practice that simply
// needs the feature turned on would be told to wait for Healthlink.
const OrderingDisabledNotice = () => (
  <Notice tone="blocking" testId="lab-orders-disabled">
    <p className={s.text}>
      Laboratory ordering isn&apos;t enabled for this practice, so lab orders
      can&apos;t be placed. It is switched on per practice — ask your administrator
      to enable it in the practice settings.
    </p>
  </Notice>
);

// Blocking: Healthlink publishes no laboratory that accepts electronic orders for
// this practice. Not a failure of ours, and not an error — but nothing can be
// ordered, so it belongs with the blocking notices.
const NoFacilitiesNotice = () => (
  <Notice tone="blocking" testId="lab-orders-no-facilities">
    <p className={s.text}>
      No hospital laboratory is available for eLab ordering yet. Healthlink
      hasn&apos;t published any laboratory that accepts electronic orders for this
      practice, so orders cannot be placed.
    </p>
  </Notice>
);

// Advisory: the developer flag that injects fake destinations is on.
const MockFacilitiesNotice = () => (
  <Notice tone="advisory" testId="lab-orders-mock-notice">
    <p className={s.text}>
      Mock laboratories are enabled for this build
      (REACT_APP_ELAB_MOCK_FACILITIES). Orders sent to them will be refused by
      Healthlink — use them to exercise the flow, not to place real orders.
    </p>
  </Notice>
);

// The stacked notices shown above the eLab section, in the same shape and order of
// precedence the CDM section uses: blocking conditions first, advice after.
//
// These are *states* of the section — why ordering is unavailable — so they belong
// above the list, not only inside the form where the GP would meet them one click
// too late. A failed attempt is not a state and is not here: it is reported as a
// toast, because it is an event, and because an in-page panel invited a raw
// transport message ("Request failed with status code 400") to sit on the screen.
export const LabOrderNotices = ({
  patientId,
  preflightViolations = [],
  isOrderingDisabled = false,
  hasNoFacilities = false,
  areMockFacilitiesEnabled = false,
}) => {
  // With ordering switched off the empty facility list is a consequence, not a second
  // problem — the backend does not even call Healthlink. Saying both would be noise.
  const showNoFacilities = hasNoFacilities && !isOrderingDisabled;

  const hasAnyNotice =
    preflightViolations.length > 0 ||
    isOrderingDisabled ||
    showNoFacilities ||
    areMockFacilitiesEnabled;

  if (!hasAnyNotice) return null;

  return (
    <div className={s.stack}>
      {preflightViolations.length > 0 && (
        <PatientDetailsNotice
          patientId={patientId}
          violations={preflightViolations}
        />
      )}
      {!!isOrderingDisabled && <OrderingDisabledNotice />}
      {!!showNoFacilities && <NoFacilitiesNotice />}
      {!!areMockFacilitiesEnabled && <MockFacilitiesNotice />}
    </div>
  );
};
